import React from "react";
import { Button,  Form, Typography } from "antd";
import { InputOTP } from "antd-input-otp";
import { useForgotPasswordContext } from "context/ForgotPasswordEnumeratorContext";
import { RoleTypes } from "enum/role-type-enum";
import { useEnumeratorPasswordOtpVerify } from "api/enumerator/ChangePassword/usePasswordOtpVerify";
import { useEnumeratorPasswordOtpRequest } from "api/enumerator/ChangePassword/usePasswordOtpRequest";
import { useRecruiterPasswordOtpVerify } from "api/recruiter/ChangePassword/usePasswordOtpVerify";
import { useRecruiterPasswordOtpRequest } from "api/recruiter/ChangePassword/usePasswordOtpRequest";

const { Text, Title } = Typography;

interface PasswordOtpVerifyProps {
  email: string;
  setCode: React.Dispatch<React.SetStateAction<string>>;
  roleType: string | null;
}

const PasswordOtpVerify: React.FC<PasswordOtpVerifyProps> = ({ email, setCode, roleType }) => {
  const [minutes, setMinutes] = React.useState(1);
  const [seconds, setSeconds] = React.useState(30);

  const [form] = Form.useForm();

  const { setIsEmailSent, setIsCodeVerified } = useForgotPasswordContext();

  const enumeratorPasswordOtpVerifyMutation = useEnumeratorPasswordOtpVerify();
  const enumeratorPasswordOtpRequestMutation = useEnumeratorPasswordOtpRequest();

  const recruiterPasswordOtpVerifyMutation = useRecruiterPasswordOtpVerify();
  const recruiterPasswordOtpRequestMutation = useRecruiterPasswordOtpRequest();

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setCode(values.otp.join(""));
    const parameters = {
      emailOrPhone: email,
      otp: values.otp.join(""),
      otpType: "CHANGE_PASSWORD",
    };

    if (roleType === RoleTypes.ENUMERATOR) {
      enumeratorPasswordOtpVerifyMutation.mutate(parameters, {
        onSuccess: () => {
          setIsEmailSent(false);
          setIsCodeVerified(true);
        },
      });
    }

    if (roleType === RoleTypes.RECRUITER) {
      recruiterPasswordOtpVerifyMutation.mutate(parameters, {
        onSuccess: () => {
          setIsEmailSent(false);
          setIsCodeVerified(true);
        },
      });
    }
  };

  const requestOtp = () => {
    setMinutes(1);
    setSeconds(30);
    const parameters = { emailOrPhone: email };
    if (roleType === RoleTypes.ENUMERATOR) {
      enumeratorPasswordOtpRequestMutation.mutate(parameters);
    }

    if (roleType === RoleTypes.RECRUITER) {
      recruiterPasswordOtpRequestMutation.mutate(parameters);
    }
  };

  React.useEffect(() => {
    const interval = setInterval(() => {
      if (seconds > 0) {
        setSeconds(seconds - 1);
      }

      if (seconds === 0) {
        if (minutes === 0) {
          clearInterval(interval);
        } else {
          setSeconds(59);
          setMinutes(minutes - 1);
        }
      }
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [seconds,minutes]);

  return (
    <>
      <Title level={2}>Enter verification code sent in your email address.</Title>
      <Text type="secondary">
        Please check your email address and enter verification code that we sent to{" "}
        <Text strong>{email}</Text>
      </Text>
      <div className="mt-12">
        <Form layout="vertical" form={form} onFinish={handleSubmit}>
          <Form.Item name="otp">
            <InputOTP autoSubmit={form} inputType="numeric" inputClassName="_otp_input_component" />
          </Form.Item>
          <Form.Item>
            <Button
              htmlType="submit"
              loading={
                roleType === RoleTypes.ENUMERATOR
                  ? enumeratorPasswordOtpVerifyMutation.isPending
                  : recruiterPasswordOtpVerifyMutation.isPending
              }
              type="primary"
              className="h-12 px-8 border border-2 border-border text-base  flex justify-center items-center gap-2 bg-primary text-white border-none w-full mt-12"
            >
              Verify code
            </Button>
          </Form.Item>
        </Form>
      </div>
      <div className="mt-8">
        <Text className="text-base">
          Didn't receive any code ? Try again after {minutes < 10 ? `0${minutes}` : minutes}:
          {seconds < 10 ? `0${seconds}` : seconds} secs.
          <Text
            style={{
              color: "blue",
              cursor: "pointer",
              pointerEvents: `${seconds > 0 || minutes > 0 ? "none" : "auto"}`,
              marginLeft: "3px",
            }}
            onClick={requestOtp}
            disabled={seconds > 0 || minutes > 0}
            strong
            className="text-base"
          >
            Resend
          </Text>
        </Text>
      </div>
    </>
  );
};
export default PasswordOtpVerify;
